import {
  ArrowUp,
  BadgeCheck,
  Check,
  CheckCircle2,
  CircleHelp,
  Coins,
  Gem,
  HeartPulse,
  Lock,
  LockOpen,
  RefreshCw,
  Shield,
  ShieldOff,
  Shuffle,
  Sparkles,
  Swords,
  Trash2,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DetailWindow } from "../DetailWindow";
import { EquipmentAffixRow } from "../EquipmentAffixRow";
import {
  type EnhancementResult,
  type EquipmentItem,
  type EquipmentSlot,
  type ItemAffix,
} from "../types";
import { AttackElementMark } from "../ui/Elements";
import { EquipmentSlotIcon, QUALITY_LABELS, SLOT_LABELS } from "../ui/EquipmentIcon";
import {
  formatAffixAmount,
  formatAffixResult,
  formatEnhancementValue,
  rollGrade,
} from "../ui/format";
import { ResourceIcon } from "../ui/Resources";

export function EquipmentDetail({
  item,
  compared,
  enhancementResult,
  busy,
  onEquip,
  onUnequip,
  onLock,
  onEnhance,
  onReforge,
  onRefine,
  onCalibrate,
  onSell,
  onClose,
}: {
  item: EquipmentItem;
  compared?: EquipmentItem;
  enhancementResult?: EnhancementResult;
  busy: boolean;
  onEquip: (itemId: string) => void;
  onUnequip: (slot: EquipmentSlot) => void;
  onLock: (itemId: string, locked: boolean) => void;
  onEnhance: (itemId: string) => void;
  onReforge: (itemId: string, affixIndex: number) => void;
  onRefine: (itemId: string, affixIndex: number) => void;
  onCalibrate: (itemId: string) => void;
  onSell: (itemIds: string[]) => void;
  onClose: () => void;
}) {
  const requirement = item.requirements ?? {
    level: 1,
    attack: 0,
    defense: 0,
    intelligence: 0,
    constitution: 0,
  };
  const requirementsMet = item.requirementsMet ?? true;
  const requirements = [
    `人物 ${requirement.level}`,
    requirement.attack > 0 ? `攻击 ${requirement.attack}` : "",
    requirement.defense > 0 ? `防御 ${requirement.defense}` : "",
    requirement.intelligence > 0 ? `智力 ${requirement.intelligence}` : "",
    requirement.constitution > 0 ? `体质 ${requirement.constitution}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const implicitAffixes = item.implicitAffixes ?? [];
  const affixes = item.affixes ?? [];
  const enhancementBonus = item.enhancementBonus ?? { attack: 0, defense: 0, hp: 0 };
  const enhancementMaxLevel = item.enhancementMaxLevel ?? 20;
  const enhanceSuccessPercent =
    item.enhanceSuccessPercent ?? Math.max(5, 100 - item.enhancementLevel * 5);
  const itemLevelDelta = compared ? item.itemLevel - compared.itemLevel : 0;
  const reforgeCostCapAt = item.reforgeCostCapAt ?? 20;
  const reforgeCostCapped = item.reforgesUsed >= reforgeCostCapAt;
  const refinementsUsed = item.refinementsUsed ?? 0;
  const refinementCostCapAt = item.refinementCostCapAt ?? 10;
  const refinementCostCapped = refinementsUsed >= refinementCostCapAt;
  const hasNumericAffix = affixes.some((affix) => affix.mode !== "mechanic");
  const pendingReforge = useRef<{
    itemId: string;
    affixIndex: number;
    reforgesUsed: number;
    name: string;
    mode: string;
    value: number;
  } | null>(null);
  const [reforgeResult, setReforgeResult] = useState<{
    itemId: string;
    affixIndex: number;
    name: string;
    mode: string;
    before: number;
    after: number;
  } | null>(null);
  const pendingRefinement = useRef<{
    itemId: string;
    affixIndex: number;
    refinementsUsed: number;
    affix: ItemAffix;
  } | null>(null);
  const [refinementResult, setRefinementResult] = useState<{
    itemId: string;
    affixIndex: number;
    before: ItemAffix;
    after: ItemAffix;
  } | null>(null);
  const [maintenanceHelp, setMaintenanceHelp] = useState<"reforge" | "refine" | null>(null);

  useEffect(() => {
    const pending = pendingReforge.current;
    if (!pending || pending.itemId !== item.id || item.reforgesUsed <= pending.reforgesUsed) return;
    const next = item.affixes?.[pending.affixIndex];
    if (next) {
      setReforgeResult({
        itemId: item.id,
        affixIndex: pending.affixIndex,
        name: pending.name,
        mode: pending.mode,
        before: pending.value,
        after: next.value,
      });
    }
    pendingReforge.current = null;
  }, [item.affixes, item.id, item.reforgesUsed]);

  useEffect(() => {
    const pending = pendingRefinement.current;
    if (!pending || pending.itemId !== item.id || refinementsUsed <= pending.refinementsUsed)
      return;
    const next = item.affixes?.[pending.affixIndex];
    if (next) {
      setRefinementResult({
        itemId: item.id,
        affixIndex: pending.affixIndex,
        before: pending.affix,
        after: next,
      });
    }
    pendingRefinement.current = null;
  }, [item.affixes, item.id, refinementsUsed]);

  const triggerReforge = (affixIndex: number) => {
    const affix = affixes[affixIndex];
    if (!affix || affix.mode === "mechanic") return;
    pendingReforge.current = {
      itemId: item.id,
      affixIndex,
      reforgesUsed: item.reforgesUsed,
      name: affix.name,
      mode: affix.mode,
      value: affix.value,
    };
    setReforgeResult(null);
    setRefinementResult(null);
    onReforge(item.id, affixIndex);
  };
  const triggerRefinement = (affixIndex: number) => {
    const affix = affixes[affixIndex];
    if (!affix) return;
    pendingRefinement.current = {
      itemId: item.id,
      affixIndex,
      refinementsUsed,
      affix: { ...affix },
    };
    setReforgeResult(null);
    setRefinementResult(null);
    onRefine(item.id, affixIndex);
  };
  const visibleReforgeResult = reforgeResult?.itemId === item.id ? reforgeResult : null;
  const visibleRefinementResult = refinementResult?.itemId === item.id ? refinementResult : null;
  const reforgeDirection = visibleReforgeResult
    ? visibleReforgeResult.after > visibleReforgeResult.before
      ? "is-higher"
      : visibleReforgeResult.after < visibleReforgeResult.before
        ? "is-lower"
        : "is-same"
    : "";
  return (
    <DetailWindow
      className={`equipment-detail equipment-modal quality-${item.quality}`}
      labelledBy="equipment-detail-title"
      closeLabel="关闭装备详情"
      onClose={onClose}
      header={
        <div className="equipment-banner">
          <div className="detail-quality">
            <span>{QUALITY_LABELS[item.quality]}品质</span>
            <b>{item.tier.toUpperCase()}</b>
          </div>
          <div className="detail-title">
            <div>
              <h3 id="equipment-detail-title">
                {item.name}
                {item.enhancementLevel > 0 ? ` +${item.enhancementLevel}` : ""}
              </h3>
              <small>
                {SLOT_LABELS[item.slot]} · 装备等级 {item.level ?? 1}
              </small>
            </div>
            <span>
              <EquipmentSlotIcon
                slot={item.slot}
                tier={item.tier}
                size={64}
                legendaryId={item.legendaryId}
                templateId={item.templateId}
              />
            </span>
          </div>
          <div className="item-level-score">
            <span>装备评分</span>
            <strong>{item.itemLevel ?? 10}</strong>
            {item.equipped && (
              <small>
                <Check size={13} />
                已装备
              </small>
            )}
          </div>
        </div>
      }
    >
      <div className="detail-section">
        <h4>基础属性</h4>
        <dl className="detail-stats">
          <div>
            <dt>
              <Swords size={16} />
              攻击 <small title="基础属性评级">{rollGrade(item.rolls?.baseAttack)}</small>
            </dt>
            <dd>
              +{item.baseAttack}
              {enhancementBonus.attack > 0 && (
                <small className="enhancement-bonus">
                  (+{formatEnhancementValue(enhancementBonus.attack)})
                </small>
              )}
            </dd>
          </div>
          <div>
            <dt>
              <Shield size={16} />
              防御 <small title="基础属性评级">{rollGrade(item.rolls?.baseDefense)}</small>
            </dt>
            <dd>
              +{item.baseDefense}
              {enhancementBonus.defense > 0 && (
                <small className="enhancement-bonus">
                  (+{formatEnhancementValue(enhancementBonus.defense)})
                </small>
              )}
            </dd>
          </div>
          <div>
            <dt>
              <HeartPulse size={16} />
              生命 <small title="基础属性评级">{rollGrade(item.rolls?.baseHp)}</small>
            </dt>
            <dd>
              +{item.baseHp}
              {enhancementBonus.hp > 0 && (
                <small className="enhancement-bonus">
                  (+{formatEnhancementValue(enhancementBonus.hp)})
                </small>
              )}
            </dd>
          </div>
        </dl>
        {item.slot === "weapon" && (
          <div className="weapon-element-line">
            <span>伤害属性</span>
            <AttackElementMark element={item.damageElement ?? "neutral"} />
          </div>
        )}
      </div>
      {compared && (
        <div className="comparison-line">
          <span>对比已装备</span>
          <strong className={itemLevelDelta >= 0 ? "is-positive" : "is-negative"}>
            {itemLevelDelta >= 0 ? "+" : ""}
            {itemLevelDelta} 装等
          </strong>
        </div>
      )}
      <div className="detail-section">
        <h4>词条与效果</h4>
        {hasNumericAffix && (
          <div className="reforge-guide">
            <div className="maintenance-summary">
              <div className="maintenance-summary-item">
                <span>
                  词条重铸{" "}
                  <button
                    type="button"
                    className="maintenance-help-button"
                    aria-label="查看词条重铸说明"
                    aria-expanded={maintenanceHelp === "reforge"}
                    aria-controls="reforge-help"
                    onClick={() =>
                      setMaintenanceHelp((current) => (current === "reforge" ? null : "reforge"))
                    }
                  >
                    <CircleHelp size={14} />
                  </button>
                </span>
                <strong>
                  {item.reforgesUsed} / {reforgeCostCapAt} 次
                </strong>
              </div>
              <div className="maintenance-summary-item">
                <span>
                  词条洗炼{" "}
                  <button
                    type="button"
                    className="maintenance-help-button"
                    aria-label="查看词条洗炼说明"
                    aria-expanded={maintenanceHelp === "refine"}
                    aria-controls="refine-help"
                    onClick={() =>
                      setMaintenanceHelp((current) => (current === "refine" ? null : "refine"))
                    }
                  >
                    <CircleHelp size={14} />
                  </button>
                </span>
                <strong>
                  {refinementsUsed} / {refinementCostCapAt} 次
                </strong>
              </div>
            </div>
            {maintenanceHelp === "reforge" && (
              <div id="reforge-help" className="maintenance-help" role="note">
                只改变所选词条的数值，不改变词条类型。每次完成后价格提高，第 {reforgeCostCapAt}{" "}
                次后价格不再上涨。
              </div>
            )}
            {maintenanceHelp === "refine" && (
              <div
                id="refine-help"
                className="maintenance-help refinement-probability-panel"
                role="note"
              >
                <p>
                  将所选词条替换为本装备尚未拥有的另一种词条；第 {refinementCostCapAt}{" "}
                  次后价格封顶。
                </p>
                {item.refinementProbabilities?.map((set) => (
                  <section key={set.affixIndex}>
                    <h5>{affixes[set.affixIndex]?.name ?? `词条 ${set.affixIndex + 1}`}</h5>
                    <div>
                      {set.options.map((option) => (
                        <span key={option.affixId}>
                          <b>{option.name}</b>
                          <em>{option.lessThanFive ? "<5%" : `${option.approxPercent}%`}</em>
                        </span>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
            <div className="maintenance-costs">
              <div className="reforge-cost">
                {item.canReforge ? (
                  <>
                    <span>重铸费用</span>
                    <b>
                      <Coins size={12} />
                      {item.reforgeCost.gold}
                    </b>
                    <b>
                      <ResourceIcon kind="crystal" size={12} />
                      {item.reforgeCost.crystal}
                    </b>
                  </>
                ) : (
                  <span>升级铁匠铺后可重铸</span>
                )}
                {reforgeCostCapped && <em>价格已达上限</em>}
              </div>
              <div className="reforge-cost">
                {item.canRefine ? (
                  <>
                    <span>洗炼费用</span>
                    <b>
                      <Coins size={12} />
                      {item.refinementCost.gold}
                    </b>
                    <b>
                      <ResourceIcon kind="crystal" size={12} />
                      {item.refinementCost.crystal}
                    </b>
                  </>
                ) : (
                  <span>铁匠铺 Lv.5 解锁洗炼</span>
                )}
                {refinementCostCapped && <em>价格已达上限</em>}
              </div>
            </div>
          </div>
        )}
        <div className="detail-affixes">
          {item.legendaryDescription && (
            <span className="legendary-effect">
              <Sparkles size={14} />
              {item.legendaryDescription}
            </span>
          )}
          {implicitAffixes.map((affix, index) => (
            <EquipmentAffixRow
              key={`implicit-${affix.id}-${index}`}
              affix={affix}
              value={formatAffixAmount(affix.mode, affix.value)}
              implicit
            />
          ))}
          {affixes.length > 0
            ? affixes.map((affix, index) => {
                const changedClass =
                  visibleReforgeResult?.affixIndex === index
                    ? `is-reforged ${reforgeDirection}`
                    : visibleRefinementResult?.affixIndex === index
                      ? "is-reforged is-refined"
                      : undefined;
                const actions =
                  (affix.mode !== "mechanic" && item.canReforge) || item.canRefine ? (
                    <>
                      {affix.mode !== "mechanic" && item.canReforge && (
                        <button
                          className="icon-button affix-reforge"
                          disabled={busy}
                          onClick={() => triggerReforge(index)}
                          title={`重铸数值：${item.reforgeCost.gold} 金币 / ${item.reforgeCost.crystal} 结晶`}
                          aria-label={`重铸${affix.name}数值`}
                        >
                          <RefreshCw size={13} />
                        </button>
                      )}
                      {item.canRefine && (
                        <button
                          className="icon-button affix-refine"
                          disabled={busy}
                          onClick={() => triggerRefinement(index)}
                          title={`洗炼词条：${item.refinementCost.gold} 金币 / ${item.refinementCost.crystal} 结晶`}
                          aria-label={`洗炼${affix.name}`}
                        >
                          <Shuffle size={13} />
                        </button>
                      )}
                    </>
                  ) : undefined;
                return (
                  <EquipmentAffixRow
                    key={`${affix.id}-${index}`}
                    affix={affix}
                    value={formatAffixAmount(affix.mode, affix.value)}
                    className={changedClass}
                    actions={actions}
                  />
                );
              })
            : !item.legendaryDescription &&
              implicitAffixes.length === 0 && <span>基础装备 · 无额外词条</span>}
        </div>
        {visibleReforgeResult && (
          <div className={`reforge-result ${reforgeDirection}`} role="status">
            <RefreshCw size={15} />
            <span>重铸完成 · {visibleReforgeResult.name}</span>
            <strong>
              {formatAffixAmount(visibleReforgeResult.mode, visibleReforgeResult.before, true)} →{" "}
              {formatAffixAmount(visibleReforgeResult.mode, visibleReforgeResult.after, true)}
            </strong>
            <small>
              {reforgeDirection === "is-higher"
                ? "提升"
                : reforgeDirection === "is-lower"
                  ? "降低"
                  : "数值未变"}
            </small>
          </div>
        )}
        {visibleRefinementResult && (
          <div className="reforge-result refinement-result" role="status">
            <Shuffle size={15} />
            <span>洗炼完成</span>
            <strong>
              {formatAffixResult(visibleRefinementResult.before)} →{" "}
              {formatAffixResult(visibleRefinementResult.after)}
            </strong>
            <small>词条已替换</small>
          </div>
        )}
      </div>
      {item.sockets && item.sockets.length > 0 && (
        <div className="detail-section detail-sockets">
          <h4>镶嵌孔</h4>
          <div className="socket-grid">
            {item.sockets.map((socket) => (
              <span className={`socket-slot${socket.gemId ? " is-filled" : ""}`} key={socket.id}>
                <Gem size={14} />
                <b>{socket.gemName ?? "空孔"}</b>
                {socket.gemId && <small>已镶嵌</small>}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className={`requirement-box${requirementsMet ? "" : " is-unmet"}`}>
        <span>穿戴需求</span>
        <strong>{requirements}</strong>
      </div>
      <div className="detail-action">
        <button
          className={`icon-button item-lock-button${item.locked ? " is-active" : ""}`}
          disabled={busy}
          onClick={() => onLock(item.id, !item.locked)}
          title={item.locked ? "解除装备锁定" : "锁定装备"}
          aria-label={item.locked ? "解除装备锁定" : "锁定装备"}
        >
          {item.locked ? <Lock size={18} /> : <LockOpen size={18} />}
        </button>
        {item.equipped ? (
          <button
            className="secondary-command compact"
            disabled={busy}
            onClick={() => onUnequip(item.slot)}
          >
            <ShieldOff size={17} />
            脱下装备
          </button>
        ) : (
          <button
            className="primary-command compact"
            disabled={busy || !requirementsMet}
            onClick={() => onEquip(item.id)}
          >
            <BadgeCheck size={17} />
            {requirementsMet ? "装备" : "需求未达"}
          </button>
        )}
      </div>
      <div className="detail-maintenance">
        <button
          className="secondary-command compact"
          disabled={busy || !item.canCalibrate}
          onClick={() => onCalibrate(item.id)}
        >
          <RefreshCw size={16} />
          {item.rolls?.calibrationUsed
            ? "基础校准已使用"
            : item.canCalibrate
              ? `基础校准 · ${item.calibrationCost?.gold ?? 0} 金币 / ${item.calibrationCost?.crystal ?? 0} 结晶`
              : "铁匠铺 Lv.3 解锁校准"}
        </button>
        {item.canEnhance && (
          <div className="enhancement-offer">
            <span>强化至 +{item.enhancementLevel + 1}</span>
            <strong>成功率 {enhanceSuccessPercent}%</strong>
            <small>
              {item.enhanceFailureLevelLoss > 0
                ? `失败降 ${item.enhanceFailureLevelLoss} 级`
                : "失败不降级"}{" "}
              · {item.enhanceCost.gold} 金币
            </small>
          </div>
        )}
        {enhancementResult && (
          <div
            className={`enhancement-result ${enhancementResult.success ? "is-success" : "is-failure"}`}
            role="status"
          >
            {enhancementResult.success ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
            <span>
              {enhancementResult.success
                ? `强化成功 · +${enhancementResult.targetLevel}`
                : enhancementResult.enhancementLevel < enhancementResult.previousLevel
                  ? `强化失败 · 降至 +${enhancementResult.enhancementLevel}`
                  : `强化失败 · 保持 +${enhancementResult.previousLevel}`}
            </span>
            <strong>{enhancementResult.successPercent}%</strong>
            <small>消耗 {enhancementResult.goldSpent} 金币</small>
          </div>
        )}
        <button
          className="secondary-command compact"
          disabled={busy || !item.canEnhance}
          onClick={() => onEnhance(item.id)}
        >
          <ArrowUp size={16} />
          {item.enhancementBusy
            ? "强化中"
            : item.canEnhance
              ? "安排强化"
              : `已强化至 +${enhancementMaxLevel}`}
        </button>
        <button
          className="danger-command compact"
          disabled={busy || !item.canSell}
          onClick={() => onSell([item.id])}
          title={
            item.canSell ? `出售并获得 ${item.sellValue.toLocaleString("zh-CN")} 金币` : "不可出售"
          }
        >
          <Trash2 size={16} />
          {item.canSell ? `出售 · +${item.sellValue.toLocaleString("zh-CN")} 金币` : "不可出售"}
        </button>
      </div>
    </DetailWindow>
  );
}
